import { D, formatMoney } from '@makila/fiscal';
import { eanSvg } from './barcode';
import { esc } from './receipt';

/** Formatos de etiqueta: folha A4 de autocolantes (3 × 8) ou rolo térmico de 50 × 30 mm. */
export const LABEL_FORMATS = {
  a4: { label: 'Folha A4 — 24 por folha (70 × 37 mm)', w: 70, h: 37 },
  roll: { label: 'Rolo térmico 50 × 30 mm', w: 50, h: 30 },
} as const;
export type LabelFormat = keyof typeof LABEL_FORMATS;

export interface LabelItem { name: string; code: string; barcode: string | null; unitPrice: string; soldByWeight: boolean; tierText?: string | null; copies: number }

/**
 * Etiquetas de preço (proposta §3, gestor de loja): nome, preço em MT (por kg
 * nos artigos ao peso), preço por quantidade e o código de barras EAN, para a
 * caixa ler. Sem EAN válido, sai o código do artigo em texto.
 */
export function labelsHtml(items: LabelItem[], format: LabelFormat, company: string): string {
  const f = LABEL_FORMATS[format];
  const one = (i: LabelItem) => {
    const svg = i.barcode ? eanSvg(i.barcode, format === 'a4' ? 11 : 9) : null;
    return `<div class="l">
      <div class="n">${esc(i.name)}</div>
      <div class="p">${formatMoney(i.unitPrice)} <small>MT${i.soldByWeight ? '/kg' : ''}</small></div>
      ${i.tierText ? `<div class="t">${esc(i.tierText)}</div>` : ''}
      <div class="b">${svg ?? `<span class="c">${esc(i.barcode ?? i.code)}</span>`}</div>
      <div class="s">${esc(company)}</div>
    </div>`;
  };
  const all = items.flatMap((i) => Array.from({ length: Math.max(0, Math.min(500, i.copies)) }, () => one(i))).join('');
  const page = format === 'a4'
    ? `@page { size: A4; margin: 10mm 0 0 0; } .grid { display: grid; grid-template-columns: repeat(3, ${f.w}mm); grid-auto-rows: ${f.h}mm; }`
    : `@page { size: ${f.w}mm ${f.h}mm; margin: 0; } .grid { display: block; } .l { page-break-after: always; }`;
  return `<!doctype html><html lang="pt"><head><meta charset="utf-8"><title>Etiquetas</title><style>
    ${page} * { box-sizing: border-box; } body { margin: 0; font-family: Arial, Helvetica, sans-serif; color: #000; }
    .l { width: ${f.w}mm; height: ${f.h}mm; padding: 2mm 3mm; overflow: hidden; display: flex; flex-direction: column; }
    .n { font-size: 10pt; font-weight: bold; line-height: 1.15; max-height: 2.3em; overflow: hidden; }
    .p { font-size: ${format === 'a4' ? 18 : 15}pt; font-weight: bold; } .p small { font-size: 9pt; }
    .t { font-size: 8pt; } .b { margin-top: auto; } .c { font-family: monospace; font-size: 10pt; } .s { font-size: 6pt; color: #444; }
  </style></head><body><div class="grid">${all}</div></body></html>`;
}

/** "A partir de 6 un.: 45,00" (o escalão mais baixo). */
export const tierText = (tiers: { minQty: string; unitPrice: string }[] | null | undefined) =>
  tiers?.length ? `A partir de ${new D(tiers[0]!.minQty).toString()} un.: ${formatMoney(tiers[0]!.unitPrice)} MT` : null;
